// One member's agent tokens — both runners, in one place.
//
// The two are deliberately shaped differently, and the panel says so rather than
// pretending they're the same thing:
//
//   Extension — ONE per person. A second token for the same browser profile is
//               indistinguishable from the first, so Regenerate replaces it.
//   Exe agent — ONE PER MACHINE, named. A tester often runs the agent on a laptop
//               and a shared bench; if they shared a token, losing the laptop
//               would force you to kill the bench too.
//
// Issuing used to be split — an admin minted extension tokens from the team page
// while testers minted their own exe tokens from their dashboard, so nobody could
// actually control who was running an agent. Both now happen here.

import { useCallback, useEffect, useState } from "react";
import {
  KeyRound, Loader2, Puzzle, Bot, Trash2, Copy, Check, RotateCcw,
} from "lucide-react";
import Modal from "@/components/dashboard/Modal";
import Button from "@/components/ui/Button";
import { toastError, toastSuccess } from "@/lib/toast";
import {
  listMemberTokens, issueExeToken, revokeMemberToken, generateToken,
} from "@/api/company";

function fmtWhen(v) {
  if (!v) return "never used";
  try {
    const d = new Date(v);
    const mins = Math.round((Date.now() - d.getTime()) / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    if (mins < 1440) return `${Math.round(mins / 60)}h ago`;
    return `${Math.round(mins / 1440)}d ago`;
  } catch (e) { return "never used"; }
}

/** A secret shown exactly once. */
function RevealOnce({ token, onDone }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="rounded-xl border border-emerald-200 bg-emerald-50/70 p-3 dark:border-emerald-500/25 dark:bg-emerald-500/10">
      <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-300">
        Copy this now
      </p>
      <p className="mt-1 text-xs text-slate-600 dark:text-slate-300">
        It is shown once and never again — only a hash is stored.
      </p>
      <div className="mt-2 flex items-center gap-2 rounded-lg border border-emerald-200 bg-white px-3 py-2 dark:border-emerald-500/20 dark:bg-ink-900">
        <span className="min-w-0 flex-1 truncate font-mono text-xs text-slate-700 dark:text-slate-200">
          {token}
        </span>
        <button
          type="button"
          onClick={() => {
            navigator.clipboard.writeText(token).then(() => {
              setCopied(true);
              setTimeout(() => setCopied(false), 2000);
            }).catch(() => toastError("Couldn't copy — select the text instead."));
          }}
          className="inline-flex shrink-0 items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-500/10 dark:text-emerald-300"
        >
          {copied ? <><Check size={13} /> Copied</> : <><Copy size={13} /> Copy</>}
        </button>
      </div>
      <button
        type="button" onClick={onDone}
        className="mt-2 text-xs font-medium text-slate-500 underline-offset-2 hover:underline"
      >
        I&apos;ve saved it
      </button>
    </div>
  );
}

function TokenRow({ token, onRevoke, busy }) {
  return (
    <div className="flex items-center gap-3 border-t border-slate-100 px-4 py-2.5 first:border-t-0 dark:border-white/5">
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-slate-700 dark:text-slate-200">
          {token.name || "Agent token"}
        </p>
        <p className="truncate font-mono text-[0.6875rem] text-slate-400">
          {token.preview}… · {fmtWhen(token.last_used_at)}
        </p>
      </div>
      <button
        type="button" onClick={() => onRevoke(token)} disabled={busy}
        title="Revoke this token"
        className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-slate-400 hover:bg-rose-50 hover:text-rose-500 disabled:opacity-40 dark:hover:bg-rose-500/10"
      >
        <Trash2 size={15} />
      </button>
    </div>
  );
}

/**
 * @param {object|null} member  the team row; null closes the drawer
 * @param {function}    onClose
 * @param {function}    onChanged  called after any write, so the table can refresh
 */
export default function TokenDrawer({ member, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [reveal, setReveal] = useState(null);      // the raw token, shown once

  const memberId = member?.id;
  const load = useCallback(async () => {
    if (!memberId) return;
    setLoading(true);
    try {
      setData(await listMemberTokens(memberId));
    } catch (e) {
      toastError((e && e.message) || "Couldn't load this member's tokens.");
      setData({ extension: [], exe: [] });
    } finally {
      setLoading(false);
    }
  }, [memberId]);

  useEffect(() => {
    setReveal(null);
    load();
  }, [load]);

  async function issue() {
    setBusy(true);
    try {
      const res = await issueExeToken(memberId, null);
      setReveal(res.token);
      await load();
      onChanged?.();
    } catch (e) {
      toastError((e && e.message) || "Couldn't issue that token.");
    } finally { setBusy(false); }
  }

  async function regenerateExtension() {
    setBusy(true);
    try {
      const res = await generateToken(memberId);
      setReveal(res.token);
      await load();
      onChanged?.();
    } catch (e) {
      toastError((e && e.message) || "Couldn't issue that token.");
    } finally { setBusy(false); }
  }

  async function revoke(token) {
    setBusy(true);
    try {
      await revokeMemberToken(memberId, token.id);
      toastSuccess("Revoked. That agent stops connecting immediately.");
      await load();
      onChanged?.();
    } catch (e) {
      toastError((e && e.message) || "Couldn't revoke that token.");
    } finally { setBusy(false); }
  }

  const ext = (data?.extension || [])[0] || null;
  // One active agent token per tester, exactly like the extension token above.
  // A token identifies the PERSON, not a machine — the agent supplies its own
  // device id — so the same key already covered every PC the tester installed on.
  // Older accounts may still hold several from when this issued one per machine;
  // they are all listed, and the next Issue revokes the lot.
  const exeTokens = data?.exe || [];
  const exe = exeTokens[0] || null;
  // Only a tester runs tests, so only a tester needs either token. Offering them
  // for a developer or a PM would be a control that grants nothing.
  const runsTests = member?.role === "tester";

  return (
    <Modal
      open={!!member}
      onClose={() => { if (!busy) onClose(); }}
      title={member ? `Tokens — ${member.name || member.email}` : ""}
      size="lg"
      footer={
        <div className="flex justify-end">
          <Button size="sm" onClick={onClose} disabled={busy}>Done</Button>
        </div>
      }
    >
      {!runsTests ? (
        <p className="rounded-xl bg-slate-50 px-4 py-3 text-sm text-slate-600 dark:bg-white/5 dark:text-slate-300">
          Only testers run tests, so only they need an agent token.
          {member?.role === "developer"
            ? " A developer reads reports and marks fixes — no agent required."
            : " A project manager plans and staffs the work — no agent required."}
        </p>
      ) : loading ? (
        <div className="flex justify-center py-8 text-slate-400">
          <Loader2 size={20} className="animate-spin" />
        </div>
      ) : (
        <div className="space-y-5">
          {reveal && <RevealOnce token={reveal} onDone={() => setReveal(null)} />}

          {/* extension — one per person -------------------------------- */}
          <section>
            <h4 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400">
              <Puzzle size={13} /> Browser extension
            </h4>
            <div className="overflow-hidden rounded-xl border border-slate-200 dark:border-white/10">
              {ext ? (
                <>
                  <TokenRow token={ext} onRevoke={revoke} busy={busy} />
                  <div className="border-t border-slate-100 px-4 py-2.5 dark:border-white/5">
                    <Button variant="outline" size="sm" onClick={regenerateExtension} disabled={busy}>
                      <RotateCcw size={14} /> Regenerate
                    </Button>
                  </div>
                </>
              ) : (
                <div className="flex items-center justify-between gap-3 px-4 py-3">
                  <span className="text-sm text-slate-400">No token yet.</span>
                  <Button size="sm" onClick={regenerateExtension} disabled={busy}>
                    <KeyRound size={14} /> Issue
                  </Button>
                </div>
              )}
            </div>
            <p className="mt-1.5 text-xs text-slate-400">
              One per person. Regenerating invalidates the old one immediately.
            </p>
          </section>

          {/* exe — one per machine ------------------------------------- */}
          <section>
            <h4 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400">
              <Bot size={13} /> Desktop agent
            </h4>
            <div className="overflow-hidden rounded-xl border border-slate-200 dark:border-white/10">
              {exe ? (
                <>
                  {exeTokens.map((t) => (
                    <TokenRow key={t.id} token={t} onRevoke={revoke} busy={busy} />
                  ))}
                  <div className="border-t border-slate-100 px-4 py-2.5 dark:border-white/5">
                    {/* Issue is deliberately absent while a token is live: a second
                        one grants nothing the first doesn't, and piling them up is
                        how this ended with four active tokens all named the same. */}
                    <Button variant="outline" size="sm" onClick={issue} disabled={busy}>
                      {busy ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />}
                      Regenerate
                    </Button>
                  </div>
                </>
              ) : (
                <div className="flex items-center justify-between gap-3 px-4 py-3">
                  <span className="text-sm text-slate-400">No token yet.</span>
                  <Button size="sm" onClick={issue} disabled={busy}>
                    {busy ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />}
                    Issue
                  </Button>
                </div>
              )}
            </div>
            <p className="mt-1.5 text-xs text-slate-400">
              One per person — the same token works on every PC they install the
              agent on. Regenerating or revoking disconnects all of them immediately.
            </p>
          </section>
        </div>
      )}
    </Modal>
  );
}
